import { Delivery, DeliveryQuickCheckOutReason } from 'api/models/Deliveries';
import { SkipImplantCheckInReason } from 'api/models/Implants';
import { OrderStatus } from 'api/models/Orders';
import { Set } from 'api/models/Sets';
import { TrayInspectionResults } from 'api/models/Trays';

export enum EventType {
  ReceiveDelivery = 'receive_delivery',

  // CHECK IN events
  StartDeliveryCheckIn = 'start_delivery_checkin',
  StartSetCheckIn = 'start_set_checkin',
  StartTrayCheckIn = 'start_tray_checkin',
  CompleteTrayCheckIn = 'complete_tray_checkin',
  CompleteSetCheckIn = 'complete_set_checkin',
  CompleteDeliveryCheckIn = 'complete_delivery_checkin',
  RestartDeliveryCheckIn = 'restart_delivery_checkin',

  // CHECK OUT events
  StartDeliveryCheckOut = 'start_delivery_checkout',
  StartSetCheckOut = 'start_set_checkout',
  StartTrayCheckOut = 'start_tray_checkout',
  CompleteTrayCheckOut = 'complete_tray_checkout',
  CompleteSetCheckOut = 'complete_set_checkout',
  CompleteDeliveryCheckOut = 'complete_delivery_checkout',
  CompleteDeliveryQuickCheckOut = 'complete_delivery_quick_checkout',

  // AD HOC events
  NotifySingleSetCheckOut = 'notify_single_set_checkout',
  NotifySingleImplantCheckOut = 'notify_single_implant_checkout',
  DeleteDelivery = 'delete_delivery',
  DeliveryCancelledSurgery = 'delivery_cancelled_surgery',
  NotifySupplierDeliveryCancelled = 'notify_supplier_delivery_cancelled',
  CreateDeliverySets = 'create_delivery_sets',
  AddDeliverySet = 'add_delivery_set',
  UpdateDeliverySetName = 'update_delivery_set_name',
  CreateDeliverySetTrays = 'create_delivery_set_trays',
  UpdateDeliverySetTrayName = 'update_delivery_set_tray_name',
  CaptureTrayImageCheckIn = 'capture_tray_image_check_in',
  CaptureTrayImageCheckOut = 'capture_tray_image_check_out',
  DecantCaptureTrayImageCheckIn = 'decant_capture_tray_image_check_in',
  DecantTrayDetailsContinue = 'decant_tray_details_continue',
  MergeSurgeonsConfirmed = 'merge_surgeons_confirmed',

  // Turnaround events
  TurnaroundSearchSurgeryOrigin = 'turnaround_search_surgery_origin',
  TurnaroundSearchSurgeryDestination = 'turnaround_search_surgery_destination',
  TurnaroundSelectSurgeryOrigin = 'turnaround_select_surgery_origin',
  TurnaroundSelectSurgeryDestination = 'turnaround_select_surgery_destination',
  TurnaroundMoveOriginEntities = 'turnaround_move_origin_entities',
  TurnaroundMoveDestinationEntities = 'turnaround_move_destination_entities',
  TurnaroundNewSurgery = 'turnaround_new_surgery',
  TurnaroundExistingSurgery = 'turnaround_existing_surgery',
  TurnaroundCancel = 'turnaround_cancel',

  // Skip Implant Processing
  SkipCheckInImplantProcessingInitiated = 'skip_check_in_implant_processing_initiated',
  SkipCheckInImplantProcessingVerifyCount = 'skip_check_in_implant_verify_count',
  SkipCheckInImplantProcessingSkipVerifyCount = 'skip_check_in_implant_dont_verify_count',
  SkipCheckInImplantProcessingConfirm = 'skip_check_in_implant_processing_confirm',
  SkipCheckInImplantProcessingExit = 'skip_check_in_implant_processing_exit',
  SkipCheckInImplantProcessingCancel = 'skip_check_in_implant_processing_cancel',
  SkipCheckOutImplantProcessingInitiated = 'skip_check_out_implant_processing_initiated',
  SkipCheckOutImplantProcessingVerifyCount = 'skip_check_out_implant_verify_count',
  SkipCheckOutImplantProcessingSkipVerifyCount = 'skip_check_out_implant_dont_verify_count',
  SkipCheckOutImplantProcessingConfirm = 'skip_check_out_implant_processing_confirm',
  SkipCheckOutImplantProcessingExit = 'skip_check_out_implant_processing_exit',
  SkipCheckOutImplantProcessingCancel = 'skip_check_out_implant_processing_cancel',
  SkipCheckOutImplantProcessingNotifySupplier = 'skip_check_out_implant_processing_notify_supplier',
}

type DeliveryId = string;
type DeliveryStatus = Delivery['status'];
type DeliveryOrderNumber = Delivery['orderNumber'];
type NumRoadCases = number | null; // this can be null when a delivery does not have shippingDetails
type NumBoxes = number | null;
type NumTubs = number | null;
type NumPelicanCases = number | null;
type NumDoubleRoadCases = number | null;
type ReceivedBy = string;
type SetDisplayCode = Set['displayCode'];
type SetKitInstanceId = Set['kitInstanceId'];
type UserId = string;
type TenantName = string;
type TrayId = string;
type AllClean = TrayInspectionResults['allClean'];
type AllFunctional = TrayInspectionResults['allFunctional'];
type AllPartsPresent = TrayInspectionResults['allPartsPresent'];
type TrayUsed = TrayInspectionResults['trayUsed'];

export type EventInformation = {
  [EventType.ReceiveDelivery]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    current_user: UserId;
    tenant_name: TenantName;
    bypassed_connected_supplier: boolean;
  };
  [EventType.StartDeliveryCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.RestartDeliveryCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteDeliveryCheckIn]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    no_road_cases: NumRoadCases;
    no_boxes: NumBoxes;
    num_tubs: NumTubs;
    no_pelican_cases: NumPelicanCases;
    no_double_road_cases: NumDoubleRoadCases;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.StartSetCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    set_id: SetKitInstanceId;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteSetCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    set_id: SetKitInstanceId;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.StartTrayCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    tray_instance_id: TrayId;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteTrayCheckIn]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: string;
    set_id: DeliveryOrderNumber;
    tray_instance_id: TrayId;
    all_clean: AllClean;
    all_functional: AllFunctional;
    all_parts_present: AllPartsPresent;
    tray_used: TrayUsed;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.StartSetCheckOut]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    set_id: SetKitInstanceId;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteSetCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    set_id: SetKitInstanceId;
    set_display_code: SetDisplayCode;
    set_kit_instance_id: SetKitInstanceId; // legacy field replaced by set_id
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.StartTrayCheckOut]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    tray_instance_id: TrayId;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteTrayCheckOut]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: string;
    set_id: SetKitInstanceId;
    tray_instance_id: TrayId;
    all_clean: AllClean;
    all_functional: AllFunctional;
    all_parts_present: AllPartsPresent;
    tray_used: TrayUsed;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.StartDeliveryCheckOut]: {
    status: DeliveryStatus;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteDeliveryCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    no_road_cases: NumRoadCases;
    no_boxes: NumBoxes;
    num_tubs: NumTubs;
    no_pelican_cases: NumPelicanCases;
    no_double_road_cases: NumDoubleRoadCases;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CompleteDeliveryQuickCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    no_road_cases: NumRoadCases;
    no_boxes: NumBoxes;
    num_tubs: NumTubs;
    no_pelican_cases: NumPelicanCases;
    no_double_road_cases: NumDoubleRoadCases;
    current_user: UserId;
    tenant_name: TenantName;
    reason: DeliveryQuickCheckOutReason;
    date: string;
  };
  [EventType.NotifySingleSetCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    set_id: SetKitInstanceId;
    set_display_code: SetDisplayCode;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.NotifySingleImplantCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    received_by: ReceivedBy;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.DeleteDelivery]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.DeliveryCancelledSurgery]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    surgery_id: string;
  };
  [EventType.NotifySupplierDeliveryCancelled]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
  };
  [EventType.CreateDeliverySets]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    number_of_sets: number;
  };
  [EventType.AddDeliverySet]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
  };
  [EventType.UpdateDeliverySetName]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    new_set_name: string;
  };
  [EventType.CreateDeliverySetTrays]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    number_of_trays: number;
  };
  [EventType.UpdateDeliverySetTrayName]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    new_tray_name: string;
  };
  [EventType.CaptureTrayImageCheckIn]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    tray_name: string;
  };
  [EventType.CaptureTrayImageCheckOut]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    tray_name: string;
  };
  [EventType.DecantCaptureTrayImageCheckIn]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    tray_name: string;
  };
  [EventType.DecantTrayDetailsContinue]: {
    delivery_id: DeliveryId;
    order_id: DeliveryOrderNumber;
    current_user: UserId;
    tenant_name: TenantName;
    set_id: SetKitInstanceId;
    tray_name: string;
  };
  [EventType.TurnaroundSearchSurgeryOrigin]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
  };
  [EventType.TurnaroundSearchSurgeryDestination]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
  };
  [EventType.TurnaroundSelectSurgeryOrigin]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
  };
  [EventType.TurnaroundSelectSurgeryDestination]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
  };
  [EventType.TurnaroundMoveOriginEntities]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
    set_ids: SetKitInstanceId[];
    has_implants: boolean;
  };
  [EventType.TurnaroundMoveDestinationEntities]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
    set_ids: SetKitInstanceId[];
    has_implants: boolean;
  };
  [EventType.TurnaroundNewSurgery]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
    set_ids: SetKitInstanceId[];
    has_implants: boolean;
  };
  [EventType.TurnaroundExistingSurgery]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_number: string;
    surgery_reference: string;
    delivery_number: string;
    surgeon: string;
    surgery_date: string;
    set_ids: SetKitInstanceId[];
    has_implants: boolean;
  };
  [EventType.TurnaroundCancel]: {
    current_user: UserId;
    tenant_name: TenantName;
    origin_supplier: string;
    origin_surgeon: string;
    origin_surgery_date: string;
    origin_surgery_id: string;
    origin_order_number: string;
    origin_delivery_number: string;
    destination_supplier: string;
    destination_surgeon: string;
    destination_surgery_date: string;
    destination_surgery_id: string;
    destination_order_number: string;
    destination_delivery_number: string;
    set_ids: SetKitInstanceId[];
    has_implants: boolean;
  };
  [EventType.SkipCheckInImplantProcessingInitiated]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null; // null for surgery flow
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckInImplantProcessingVerifyCount]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckInImplantProcessingSkipVerifyCount]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckInImplantProcessingConfirm]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
    reason: SkipImplantCheckInReason;
    count: number | null;
    date: string;
  };
  [EventType.SkipCheckInImplantProcessingExit]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckInImplantProcessingCancel]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckOutImplantProcessingInitiated]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus;
  };
  [EventType.SkipCheckOutImplantProcessingVerifyCount]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckOutImplantProcessingSkipVerifyCount]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckOutImplantProcessingConfirm]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
    reason: SkipImplantCheckInReason;
    count: number | null;
    date: string;
  };
  [EventType.SkipCheckOutImplantProcessingExit]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckOutImplantProcessingCancel]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
  };
  [EventType.SkipCheckOutImplantProcessingNotifySupplier]: {
    current_user: UserId;
    tenant_name: TenantName;
    supplier: string;
    order_record_id: string;
    delivery_id: string | null;
    surgeon: string;
    surgery_date: string;
    status: DeliveryStatus | OrderStatus;
    num_road_cases: number;
    num_boxes: number;
    num_tubs: number;
    returned_by_rep_id?: string;
  };
  [EventType.MergeSurgeonsConfirmed]: {
    keeper_id: string;
    merge_ids: string[];
  };
};
